import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff, LockKeyhole, Mail } from "lucide-react";
import "./Login.css";
import API_URL from "../../src/Config";
import { clearAuthStorage, hasActiveSession } from "../utils/authUtils";

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (hasActiveSession()) {
      navigate("/welcome-page");
    } else {
      clearAuthStorage();
    }
  }, [navigate]);

  useEffect(() => {
    if (!alertMessage) return undefined;
    const timer = setTimeout(() => {
      setAlertMessage("");
      setAlertType("");
    }, 3000);
    return () => clearTimeout(timer);
  }, [alertMessage]);

  const handleSignIn = async () => {
    setUsernameError("");
    setPasswordError("");
    setAlertMessage("");

    if (!email.trim()) {
      setUsernameError("Enter your email.");
      return;
    }
    if (!password.trim()) {
      setPasswordError("Enter your password.");
      return;
    }

    try {
      setLoading(true);
      clearAuthStorage();
      const response = await axios.post(`${API_URL}/api/login`, { email, password });

      if (response.status !== 200) {
        setAlertMessage("Unexpected response from server");
        setAlertType("error");
        return;
      }

      const { access_token, refresh_token, user_id, company_id, role_name, message } = response.data;
      localStorage.setItem("authKey", access_token);
      localStorage.setItem("refresh_token", refresh_token);
      localStorage.setItem("user_id", user_id);
      localStorage.setItem("email", email);
      localStorage.setItem("role_name", role_name);
      localStorage.setItem("company_id", company_id);

      onLogin?.(access_token, refresh_token, email, user_id, role_name);
      setAlertMessage(message);
      setAlertType("success");

      const normalizedRole = role_name.toLowerCase();
      navigate(normalizedRole === "field technician" || normalizedRole === "admin" ? "/welcome-page" : "/Projects");
    } catch (error) {
      console.error("Error during sign-in:", error);
      setAlertMessage("Invalid credentials!");
      setAlertType("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <main className="login-container">
        <div className="login-video-section" aria-hidden="true">
          <video autoPlay muted loop playsInline className="background-video">
            <source src="/assests/jecon.mp4" type="video/mp4" />
          </video>
          <div className="video-overlay" />
        </div>

        <div className="login-shell">
          <div className="login-intro" aria-hidden="true" />

          <section className="form-section" aria-label="Sign in">
            <div className="login-card">
              <div className="card-header">
                <img src="/jecon_taros.png" alt="JECON Engineers" className="logo" />
              </div>
              <div className="card-body">
                <div className="welcome-text">
                  <h2>Welcome back</h2>
                  <p>Sign in to continue to your workspace</p>
                </div>

                {alertMessage && (
                  <div className={`alert-message ${alertType === "success" ? "alert-success" : "alert-error"}`} role="alert">
                    {alertMessage}
                  </div>
                )}

                <form onSubmit={(event) => { event.preventDefault(); handleSignIn(); }} className="login-form">
                  <div className="form-field">
                    <label htmlFor="email">Email</label>
                    <div className="input-wrapper">
                      <input id="email" type="email" placeholder="Enter your email address" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} className={usernameError ? "has-error" : ""} />
                      <span className="input-icon"><Mail aria-hidden="true" /></span>
                    </div>
                    {usernameError && <span className="error-text">{usernameError}</span>}
                  </div>

                  <div className="form-field">
                    <div className="password-label-row">
                      <label htmlFor="password">Password</label>
                      <button type="button" onClick={() => navigate("/ForgotPassword")} className="forgot-password-link">Forgot password?</button>
                    </div>
                    <div className="input-wrapper">
                      <input id="password" type={showPassword ? "text" : "password"} placeholder="Enter your password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} className={passwordError ? "has-error" : ""} />
                      <LockKeyhole className="password-leading-icon" aria-hidden="true" />
                      <button type="button" className="password-toggle" onClick={() => setShowPassword((visible) => !visible)} aria-label={showPassword ? "Hide password" : "Show password"} title={showPassword ? "Hide password" : "Show password"}>
                        {showPassword ? <Eye aria-hidden="true" /> : <EyeOff aria-hidden="true" />}
                      </button>
                    </div>
                    {passwordError && <span className="error-text">{passwordError}</span>}
                  </div>

                  <button type="submit" className="submit-button" disabled={loading}>{loading ? "Signing in..." : "Sign in"}</button>
                </form>
              </div>
              <p className="support-copy">Need help? Contact your system administrator.</p>
            </div>
          </section>
        </div>
      </main>
    </>
  );
};

export default Login;
